import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '@gbv/ui';
import { useAuth } from './stores/auth';
import { AppShell } from './shell/AppShell';
import { LoginPage } from './pages/LoginPage';

// Route-level splitting keeps the initial bundle under the Section 14 budget.
const DashboardPage = lazy(() => import('./modules/dashboard/DashboardPage'));
const LeadsPage = lazy(() => import('./modules/sales/LeadsPage'));
const LeadDetailPage = lazy(() => import('./modules/sales/LeadDetailPage'));
const ClientFilesPage = lazy(() => import('./modules/operations/ClientFilesPage'));
const ClientChatsPage = lazy(() => import('./modules/operations/ClientChatsPage'));
const DailyReportPage = lazy(() => import('./modules/operations/DailyReportPage'));
const AppointmentsPage = lazy(() => import('./modules/operations/AppointmentsPage'));
const TasksPage = lazy(() => import('./modules/operations/TasksPage'));
const MailsPage = lazy(() => import('./modules/operations/MailsPage'));
const TeamChatPage = lazy(() => import('./modules/operations/TeamChatPage'));
const AnalyticsPage = lazy(() => import('./modules/analytics/AnalyticsPage'));
const WebsitesPage = lazy(() => import('./modules/marketing/WebsitesPage'));
const CallPage = lazy(() => import('./modules/calls/CallPage'));
const VerificationPage = lazy(() => import('./modules/operations/VerificationPage'));
const DeletedItemsPage = lazy(() => import('./modules/operations/DeletedItemsPage'));
const SocialPage = lazy(() => import('./modules/marketing/SocialPage'));
const DesignPage = lazy(() => import('./modules/design/DesignPage'));
const InternalTasksPage = lazy(() => import('./modules/internal/InternalTasksPage'));
const InternalCalendarPage = lazy(() => import('./modules/internal/InternalCalendarPage'));
const InternalNotesPage = lazy(() => import('./modules/internal/InternalNotesPage'));
const QuickNotesPage = lazy(() => import('./modules/internal/QuickNotesPage'));
const InternalDirectoryPage = lazy(() => import('./modules/internal/InternalDirectoryPage'));
const InternalMailPage = lazy(() => import('./modules/internal/InternalMailPage'));
const CompaniesPage = lazy(() => import('./modules/crm/CompaniesPage'));
const PeoplePage = lazy(() => import('./modules/crm/PeoplePage'));
const CompanyDetailPage = lazy(() => import('./modules/crm/CompanyDetailPage'));
const PersonDetailPage = lazy(() => import('./modules/crm/PersonDetailPage'));
const OpportunitiesPage = lazy(() => import('./modules/crm/OpportunitiesPage'));
const OpportunityDetailPage = lazy(() => import('./modules/crm/OpportunityDetailPage'));
const InvoicesPage = lazy(() => import('./modules/finance/InvoicesPage'));
const DocumentsPage = lazy(() => import('./modules/sales/DocumentsPage'));
const OfficePage = lazy(() => import('./modules/office/OfficePage'));
const InvoiceDetailPage = lazy(() => import('./modules/finance/InvoiceDetailPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const InternalHistoryPage = lazy(() => import('./modules/internal/InternalHistoryPage'));
const InternalRemindersPage = lazy(() => import('./modules/internal/InternalRemindersPage'));

function FullPageSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-surface-page text-ink-soft">
      <Spinner size="lg" />
    </div>
  );
}

/**
 * Restores the session from the refresh cookie before deciding whether to show
 * the login screen. Without this, a reload would bounce an authenticated user
 * to /login for a moment, which reads as a bug.
 */
function RequireAuth() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);
  const location = useLocation();

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location }} />;

  return (
    <AppShell>
      <Suspense fallback={<FullPageSpinner />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function RedirectIfAuthenticated() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <LoginPage />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <RedirectIfAuthenticated /> },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'leads', element: <LeadsPage /> },
      { path: 'leads/:id', element: <LeadDetailPage /> },
      { path: 'drive', element: <ClientFilesPage /> },
      { path: 'files', element: <ClientFilesPage /> },
      { path: 'chats', element: <ClientChatsPage /> },
      { path: 'report', element: <DailyReportPage /> },
      { path: 'companies', element: <CompaniesPage /> },
      { path: 'companies/:id', element: <CompanyDetailPage /> },
      { path: 'people', element: <PeoplePage /> },
      { path: 'people/:id', element: <PersonDetailPage /> },
      { path: 'contacts', element: <PeoplePage /> },
      { path: 'opportunities', element: <OpportunitiesPage /> },
      { path: 'opportunities/:id', element: <OpportunityDetailPage /> },
      { path: 'appointments', element: <AppointmentsPage /> },
      { path: 'tasks', element: <TasksPage /> },
      { path: 'mails', element: <MailsPage /> },
      { path: 'team', element: <TeamChatPage /> },
      { path: 'analytics', element: <AnalyticsPage /> },
      { path: 'calls', element: <CallPage /> },
      { path: 'verification', element: <VerificationPage /> },
      { path: 'deleted', element: <DeletedItemsPage /> },
      { path: 'websites', element: <WebsitesPage /> },
      { path: 'social', element: <SocialPage /> },
      { path: 'design', element: <DesignPage /> },
      // GoBeeVir's own work — no client scope.
      { path: 'internal/tasks', element: <InternalTasksPage /> },
      { path: 'internal/calendar', element: <InternalCalendarPage /> },
      { path: 'internal/notes', element: <InternalNotesPage /> },
      { path: 'internal/quick-notes', element: <QuickNotesPage /> },
      { path: 'internal/directory', element: <InternalDirectoryPage /> },
      { path: 'internal/mails', element: <InternalMailPage /> },
      { path: 'calendar', element: <AppointmentsPage /> },
      { path: 'documents', element: <DocumentsPage /> },
      { path: 'office', element: <OfficePage /> },
      { path: 'invoices', element: <InvoicesPage /> },
      { path: 'invoices/:id', element: <InvoiceDetailPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'internal/history', element: <InternalHistoryPage /> },
      { path: 'internal/reminders', element: <InternalRemindersPage /> },
      { path: '*', element: <ComingSoonPage /> },
    ],
  },
]);
